// src/pages/CustomerLoans.js

import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Typography, 
    Button, 
    Paper, 
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    Menu,
    Avatar,
    Card,
    CardContent,
    CircularProgress,
    Tooltip,
    Alert,
    Snackbar,
    InputAdornment,
    Tabs,
    Tab,
    ListItemIcon,
    ListItemText,
    Autocomplete,
    FormHelperText
} from '@mui/material';
import { 
    Delete, 
    Search,
    Receipt,
    Close as CloseIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Today as TodayIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    PictureAsPdf,
    TableChart, 
    GetApp,
    Print,
    Person,
    Phone,
    CalendarMonth,
    AccountBalance,
    Money,
    MoneyOff,
    TrendingUp,
    TrendingDown,
    Euro,
    LocalAtm,
    Paid,
    Add as AddIcon,
    Payment,
    AttachMoney,
    History,
    Description,
    Note,
    MoreVert,
    Scale
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIOrdersLoans,
    APIOrdersLoanStat,
    APICustomerLoanPayments,
    APICustomerLoansPrint,
    APICustomerLoansExportExcel,
    APICustomerLoansExportPDF,
    APICustomerLoanAddPayment,
    APIUnitName,
    APIProductsList
} from '../../apiServices/APIs';
import { formatCreatedAtDate, recordsCount, recordsPerPage } from '../../utils/helpers';
import { getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import { usePermission } from '../../hooks/usePermission';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorrState';
import SystemInformation from '../../components/SystemInformation';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const CustomerLoans = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
    const [selectedLoanForAction, setSelectedLoanForAction] = useState(null);
    const [pageState, setPageState] = useState({
        ordersData: [],
        loanStats: null,
        payments: [],
        selectedOrderPayments: [],
        productsData: [],
        unitsData: []
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    
    // Search input states for autocomplete
    const [loanSearchInput, setLoanSearchInput] = useState('');
    
    const [filters, setFilters] = useState({
        customerName: '',
        customerPhone: '',
        orderStatus: '',
        dateFrom: '',
        dateTo: ''
    });

    // Payment form state
    const [paymentForm, setPaymentForm] = useState({
        order_id: '',
        amount: '',
        payment_method: 'cash',
        notes: '',
        receipt_number: '',
        payment_date: new Date().toISOString().split('T')[0]
    });

    const [paymentErrors, setPaymentErrors] = useState({});
    const [openAddPayment, setOpenAddPayment] = useState(false);
    const [isGenericPayment, setIsGenericPayment] = useState(false);
    const [openViewPayments, setOpenViewPayments] = useState(false);
    const [filterOptions, setFilterOptions] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const { hasPermission, loading: permissionLoading } = usePermission();

    // Helper Functions initialized early to prevent reference errors during render computation
    
    // Calculate total payments for an order
    const getTotalPayments = (orderId) => {
        const orderPayments = pageState.payments.filter(p => p.order_id === orderId);
        return orderPayments.reduce((total, payment) => total + parseFloat(payment.amount || 0), 0);
    };

    // Calculate remaining balance
    const getRemainingBalance = (order) => {
        const totalPaid = getTotalPayments(order.id) + parseFloat(order.customer_given_cash || 0);
        return Math.max(0, parseFloat(order.total_amount || 0) - totalPaid);
    };

    // Check if loan is fully paid
    const isLoanFullyPaid = (order) => {
        return getRemainingBalance(order) <= 0;
    };

    // Helper function to get unit name
    const getUnitName = (productId) => {
        if (!productId) return 'N/A';
        const product = pageState.productsData.find(p => p.id === productId);
        if (!product || !product.unit_id) return 'N/A';
        const unit = pageState.unitsData.find(u => u.id === product.unit_id);
        return unit ? `${unit.name} (${unit.code})` : 'N/A';
    };

    // Helper function to get unit code
    const getUnitCode = (productId) => {
        if (!productId) return 'N/A';
        const product = pageState.productsData.find(p => p.id === productId);
        if (!product || !product.unit_id) return 'N/A';
        const unit = pageState.unitsData.find(u => u.id === product.unit_id);
        return unit ? unit.code : 'N/A';
    };

    // Helper function to get product name
    const getProductName = (productId) => {
        if (!productId) return 'N/A';
        const product = pageState.productsData.find(p => p.id === productId);
        return product ? product.name : 'N/A';
    };

    useEffect(() => {
        apiService.setToastFunction(showToast);
        fetchData();
        fetchLoanStats();
        fetchProductsAndUnits();
    }, [showToast]);

    // Fetch products and units
    const fetchProductsAndUnits = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const [productsResult, unitsResult] = await Promise.all([
                apiService.list(userBranchId, APIProductsList),
                apiService.list(userBranchId, APIUnitName)
            ]);
            
            if (productsResult.success) {
                const products = Array.isArray(productsResult.data) ? productsResult.data : [];
                setPageState(prev => ({ ...prev, productsData: products }));
            }
            
            if (unitsResult.success) {
                const units = Array.isArray(unitsResult.data) ? unitsResult.data : [];
                setPageState(prev => ({ ...prev, unitsData: units }));
            }
        } catch (error) {
            console.error("Error fetching products and units:", error);
        }
    };

    // Fetch orders/loans
    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APIOrdersLoans);

            if (result.success) {
                const orders = Array.isArray(result.data) ? result.data : [];
                setPageState(prev => ({
                    ...prev,
                    ordersData: orders
                }));
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load loan data");
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch loan statistics
    const fetchLoanStats = async () => {
        setStatsLoading(true);
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.listStats(userBranchId, APIOrdersLoanStat);
            if (result.success && result.data) {
                setPageState(prev => ({
                    ...prev,
                    loanStats: result.data
                }));
            }
        } catch (error) {
            console.error("Error fetching loan statistics:", error);
        } finally {
            setStatsLoading(false);
        }
    };

    // Fetch payments for a specific order
    const fetchOrderPayments = async (orderId) => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, `${APICustomerLoanPayments}/${orderId}`);
            
            if (result.success) {
                const payments = Array.isArray(result.data) ? result.data : [];
                setPageState(prev => ({
                    ...prev,
                    selectedOrderPayments: payments,
                    payments: [...prev.payments.filter(p => p.order_id !== orderId), ...payments]
                }));
            }
        } catch (error) {
            console.error("Failed to fetch payments:", error);
        }
    };

    // Export handlers
    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };

    const handlePrint = async (record) => {
        if (!hasPermission('PRINT')) {
            showToast("Permission Denied", "You don't have permission to print loan details", "error");
            return;
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('print_id', record.id);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            const result = await apiService.print(APICustomerLoansPrint, formDataToSend);

            if (result && result.success) {
                showToast("Success", "PDF file generated successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to generate PDF file", "error");
            }
        } catch (error) {
            console.error("Print error:", error);
            showToast("Error", "Failed to generate PDF file", "error");
        }
    };

    const handleExportExcel = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export loan data", "error");
            handleExportClose();
            return;
        }

        try {
            handleExportClose();
            showToast("Processing", "Preparing Excel export...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            if (filters.customerName) formDataToSend.append('customer_name', filters.customerName);
            if (filters.customerPhone) formDataToSend.append('customer_phone', filters.customerPhone);
            if (filters.orderStatus) formDataToSend.append('order_status', filters.orderStatus);
            if (filters.dateFrom) formDataToSend.append('date_from', filters.dateFrom);
            if (filters.dateTo) formDataToSend.append('date_to', filters.dateTo);
            
            const result = await apiService.exportToExcel(APICustomerLoansExportExcel, formDataToSend);
            if (result && result.success) {
                showToast("Success", "Excel file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export Excel file", "error");
            }
        } catch (error) {
            console.error('Excel export error:', error);
            showToast("Error", error.message || "Failed to export Excel file", "error");
        }
    };

    const handleExportPDF = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export loan data", "error");
            handleExportClose();
            return;
        }

        try {
            handleExportClose();
            showToast("Processing", "Preparing PDF export...", "info");

            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            if (filters.customerName) formDataToSend.append('customer_name', filters.customerName);
            if (filters.orderStatus) formDataToSend.append('order_status', filters.orderStatus);
            
            const result = await apiService.exportToPDF(APICustomerLoansExportPDF, formDataToSend);

            if (result && result.success) {
                showToast("Success", "PDF file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export PDF file", "error");
            }
        } catch (error) {
            console.error('PDF export error:', error);
            showToast("Error", error.message || "Failed to export PDF file", "error");
        }
    };

    // Filter based on search term and filters
    const filteredData = pageState.ordersData.filter(order => {
        const matchesSearch = searchTerm === '' ||
            order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.customer_phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.id?.toString().includes(searchTerm);

        const matchesCustomerName = !filters.customerName ||
            order.customer_name?.toLowerCase().includes(filters.customerName.toLowerCase());
        
        const matchesCustomerPhone = !filters.customerPhone ||
            order.customer_phone?.toLowerCase().includes(filters.customerPhone.toLowerCase());
        
        const matchesOrderStatus = !filters.orderStatus ||
            order.order_status === filters.orderStatus;

        let matchesDate = true;
        if (filters.dateFrom) {
            const orderDate = new Date(order.created_at).toISOString().split('T')[0];
            const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
            matchesDate = matchesDate && orderDate >= filterFromDate;
        }

        if (filters.dateTo) {
            const orderDate = new Date(order.created_at).toISOString().split('T')[0];
            const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
            matchesDate = matchesDate && orderDate <= filterToDate;
        }

        return matchesSearch && matchesCustomerName && matchesCustomerPhone && 
               matchesOrderStatus && matchesDate;
    });

    // Calculate totals for filtered data
    const calculateFilteredTotals = () => {
        const totalLoanAmount = filteredData.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);
        const totalPaid = filteredData.reduce((sum, order) => {
            const paid = getTotalPayments(order.id) + parseFloat(order.customer_given_cash || 0);
            return sum + paid;
        }, 0);
        const totalOutstanding = filteredData.reduce((sum, order) => sum + getRemainingBalance(order), 0);
        const paidLoansCount = filteredData.filter(order => isLoanFullyPaid(order)).length;
        const pendingLoansCount = filteredData.filter(order => !isLoanFullyPaid(order)).length;

        return {
            totalLoanAmount,
            totalPaid,
            totalOutstanding,
            paidLoansCount,
            pendingLoansCount,
            totalCount: filteredData.length
        };
    };

    const filteredTotals = calculateFilteredTotals();

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Dialog handlers
    const handleFilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const handleOpenView = (order) => {
        setSelectedRecord(order);
        setOpenView(true);
        fetchOrderPayments(order.id);
        handleActionMenuClose();
    };

    const handleCloseView = () => {
        setOpenView(false);
        setActiveTab(0);
    };

    // Payment dialog handlers
    const handleOpenAddPayment = (order = null) => {
        if (!hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add payments", "error");
            return;
        }

        if (order) {
            setSelectedRecord(order);
            setIsGenericPayment(false);
            setPaymentForm({
                order_id: order.id,
                amount: '',
                payment_method: 'cash',
                notes: '',
                receipt_number: '',
                payment_date: new Date().toISOString().split('T')[0]
            });
            setLoanSearchInput(order.customer_name);
        } else {
            setSelectedRecord(null);
            setIsGenericPayment(true);
            setPaymentForm({
                order_id: '',
                amount: '',
                payment_method: 'cash',
                notes: '',
                receipt_number: '',
                payment_date: new Date().toISOString().split('T')[0]
            });
            setLoanSearchInput('');
        }
        setPostStatus("ADD");
        setOpenAddPayment(true);
        handleActionMenuClose();
    };

    const handleCloseAddPayment = () => {
        setOpenAddPayment(false);
        setPaymentErrors({});
        setLoanSearchInput('');
        if (!openView) {
            setSelectedRecord(null);
        }
    };

    const handleOpenViewPayments = (order) => {
        setSelectedRecord(order);
        fetchOrderPayments(order.id);
        setOpenViewPayments(true);
        handleActionMenuClose();
    };

    const handleCloseViewPayments = () => {
        setOpenViewPayments(false);
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Payment form handlers
    const handlePaymentFormChange = (e) => {
        const { name, value } = e.target;
        setPaymentForm(prev => ({
            ...prev,
            [name]: value
        }));

        if (paymentErrors[name]) {
            setPaymentErrors(prev => ({
                ...prev,
                [name]: false
            }));
        }
    };

    // Handler for loan selection in autocomplete
    const handleLoanSelect = (event, selectedLoan) => {
        if (selectedLoan) {
            setSelectedRecord(selectedLoan);
            setPaymentForm(prev => ({
                ...prev,
                order_id: selectedLoan.id
            }));
            setLoanSearchInput(selectedLoan.customer_name);
        } else {
            setSelectedRecord(null);
            setPaymentForm(prev => ({
                ...prev,
                order_id: ''
            }));
            setLoanSearchInput('');
        }
    };

    const validatePaymentForm = () => {
        if (!paymentForm.order_id && !selectedRecord) {
            showToast("Validation Error", "Please select a loan", "warning");
            return false;
        }
        if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
            showToast("Validation Error", "Amount must be greater than 0", "warning");
            return false;
        }
        if (!paymentForm.payment_method) {
            showToast("Validation Error", "Payment method is required", "warning");
            return false;
        }
        return true;
    };

    const handleSubmitPayment = async (e) => {
        e.preventDefault();

        if (!hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add payments", "error");
            return;
        }

        if (!validatePaymentForm()) return;

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));
        formDataToSend.append('order_id', selectedRecord?.id);
        formDataToSend.append('amount', paymentForm.amount);
        formDataToSend.append('payment_method', paymentForm.payment_method);
        formDataToSend.append('notes', paymentForm.notes || '');
        formDataToSend.append('receipt_number', paymentForm.receipt_number || '');
        formDataToSend.append('payment_date', paymentForm.payment_date);

        try {
            const result = await apiService.create(APICustomerLoanAddPayment, formDataToSend);

            if (result && result.success) {
                showToast("Success", "Payment added successfully", "success");
                
                fetchData();
                fetchLoanStats();
                
                if (selectedRecord) {
                    fetchOrderPayments(selectedRecord.id);
                }
                
                handleCloseAddPayment();
                
                setPaymentForm({
                    order_id: '',
                    amount: '',
                    payment_method: 'cash',
                    notes: '',
                    receipt_number: '',
                    payment_date: new Date().toISOString().split('T')[0]
                });
            }
        } catch (error) {
            console.error("API Error:", error);
        }
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({
            ...filters,
            dateFrom: today,
            dateTo: today
        });
        handleCloseOptions();
    };

    const applyThisMonthFilter = () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

        setFilters({
            ...filters,
            dateFrom: firstDay,
            dateTo: lastDay
        });
        handleCloseOptions();
    };

    const clearFilters = () => {
        setFilters({
            customerName: '',
            customerPhone: '',
            orderStatus: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    const applyFilters = () => {
        handleCloseOptions();
    };

    // Action menu handlers
    const handleActionMenuOpen = (event, loan) => {
        event.stopPropagation();
        setActionMenuAnchorEl(event.currentTarget);
        setSelectedLoanForAction(loan);
    };

    const handleActionMenuClose = () => {
        setActionMenuAnchorEl(null);
        setSelectedLoanForAction(null);
    };

    // Get status color
    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'success';
            case 'pending': return 'warning';
            case 'quotation': return 'info';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    // Get balance color
    const getBalanceColor = (balance) => {
        return parseFloat(balance) >= 0 ? 'success.main' : 'error.main';
    };

    // Get balance icon
    const getBalanceIcon = (balance) => {
        return parseFloat(balance) >= 0 ? <TrendingUp /> : <TrendingDown />;
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-UG', {
            style: 'currency',
            currency: 'UGX',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    // Refresh all data
    const handleRefreshAll = () => {
        fetchData();
        fetchLoanStats();
    };

    if (permissionLoading) {
        return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    }

    if (loading) {
        return (
            <LoadingState
                message="Loading Loan Data..."
                variant="medical"
                size="large"
                fullPage
            />
        );
    }

    if (error) {
        return (
            <ErrorState
                error={error}
                onRetry={fetchData}
                title="Unable to Load Loan Data"
                variant="medical"
                fullPage
                retryText="Retry Loading"
            />
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Title */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 4,
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AccountBalance color="primary" sx={{ fontSize: 30, mr: 2 }} />
                    <Typography sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main
                    }}>
                        Customer Loans Management
                    </Typography>
                </Box>

                <Typography variant="subtitle1" sx={{
                    fontWeight: 'bold',
                    color: theme.palette.primary.main,
                    flexShrink: 0,
                    width: { xs: '100%', sm: 'auto' },
                    mb: { xs: 1, sm: 0 }
                }}>
                    {recordsCount(pageState.ordersData.length)} Total Loans
                </Typography>
            </Box>

            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Total Loans Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Total Loans</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : 
                                        (pageState.loanStats?.total_loans || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.success.light, width: 56, height: 56 }}>
                                <LocalAtm sx={{ fontSize: 32 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>

                {/* Total Loan Amount Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : 
                                        formatCurrency(pageState.loanStats?.total_loan_amount || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.info.light, width: 56, height: 56 }}>
                                <Money sx={{ fontSize: 32 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>

                {/* Outstanding Amount Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Outstanding</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : 
                                        formatCurrency(pageState.loanStats?.total_outstanding || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.error.light, width: 56, height: 56 }}>
                                <MoneyOff sx={{ fontSize: 32 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>

                {/* Pending Loans Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Pending</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : 
                                        (pageState.loanStats?.pending_loans || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.warning.light, width: 56, height: 56 }}>
                                <Paid sx={{ fontSize: 32 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Search and Filter Bar */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                        flexShrink: 0
                    }}>
                        Loan Management
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {hasPermission('ADD') && (
                            <Tooltip title="Add payment to a loan">
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<Payment />}
                                    onClick={() => handleOpenAddPayment()}
                                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                                    size="small"
                                >
                                    Add Payment
                                </Button>
                            </Tooltip>
                        )}

                        {hasPermission('VIEW') && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={handleFilterOptions}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                                size="small"
                            >
                                View Options
                            </Button>
                        )}

                        {hasPermission('EXPORT') && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                                size="small"
                            >
                                Export Data
                            </Button>
                        )}

                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefreshAll}
                            sx={{ textTransform: 'none', borderRadius: '8px' }}
                            size="small"
                        >
                            Refresh
                        </Button>
                    </Box>

                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search loans..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {/* Active Filters */}
                {(filters.customerName || filters.customerPhone || filters.orderStatus || filters.dateFrom || filters.dateTo) && (
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.customerName && (
                            <Chip
                                label={`Customer: ${filters.customerName}`}
                                onDelete={() => setFilters(prev => ({ ...prev, customerName: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.customerPhone && (
                            <Chip
                                label={`Phone: ${filters.customerPhone}`}
                                onDelete={() => setFilters(prev => ({ ...prev, customerPhone: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.orderStatus && (
                            <Chip
                                label={`Status: ${filters.orderStatus}`}
                                onDelete={() => setFilters(prev => ({ ...prev, orderStatus: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.dateFrom && (
                            <Chip
                                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.dateTo && (
                            <Chip
                                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        <Button
                            size="small"
                            onClick={clearFilters}
                            startIcon={<CloseIcon fontSize="small" />}
                            sx={{ ml: 1 }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Export Menu */}
            <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="success" />
                    Export to Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
                    <PictureAsPdf fontSize="small" color="error" />
                    Export to PDF (.pdf)
                </MenuItem>
            </Menu>

            {/* Action Menu for List Items */}
            <Menu
                anchorEl={actionMenuAnchorEl}
                open={Boolean(actionMenuAnchorEl)}
                onClose={handleActionMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {hasPermission('VIEW') && (
                    <MenuItem onClick={() => selectedLoanForAction && handleOpenView(selectedLoanForAction)}>
                        <ListItemIcon><ViewIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText sx={{ color: 'primary.main' }}>View Details</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('PRINT') && (
                    <MenuItem onClick={() => selectedLoanForAction && handlePrint(selectedLoanForAction)}>
                        <ListItemIcon><Print fontSize="small" color="warning" /></ListItemIcon>
                        <ListItemText sx={{ color: 'warning.main' }}>Print</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('ADD') && selectedLoanForAction && !isLoanFullyPaid(selectedLoanForAction) && (
                    <MenuItem onClick={() => selectedLoanForAction && handleOpenAddPayment(selectedLoanForAction)}>
                        <ListItemIcon><AttachMoney fontSize="small" color="success" /></ListItemIcon>
                        <ListItemText sx={{ color: 'success.main' }}>Add Payment</ListItemText>
                    </MenuItem>
                )}
                <MenuItem onClick={() => selectedLoanForAction && handleOpenViewPayments(selectedLoanForAction)}>
                    <ListItemIcon><History fontSize="small" color="info" /></ListItemIcon>
                    <ListItemText sx={{ color: 'info.main' }}>Payment History</ListItemText>
                </MenuItem>
            </Menu>

            {/* Loans Table */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    '& th': {
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.95rem'
                                    }
                                }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Customer</TableCell>
                                    <TableCell>Phone</TableCell>
                                    <TableCell align="right">Loan Amount</TableCell>
                                    <TableCell align="right">Remaining Balance</TableCell>
                                    <TableCell>Payment Method</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((order, index) => {
                                    const remainingBalance = getRemainingBalance(order);
                                    const isFullyPaid = remainingBalance <= 0;
                                    return (
                                        <TableRow
                                            key={order.id}
                                            hover
                                            sx={{
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: theme.palette.action.hover
                                                },
                                                backgroundColor: isFullyPaid ? theme.palette.success.light + '20' : 'inherit'
                                            }}
                                        >
                                            <TableCell>
                                                <Chip
                                                    label={page * rowsPerPage + index + 1}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <CalendarMonth color="action" sx={{ mr: 1, fontSize: 16 }} />
                                                    {formatCreatedAtDate(order.created_at)}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'medium' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Person color="action" sx={{ mr: 1, fontSize: 16 }} />
                                                    {order.customer_name}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={<Phone />}
                                                    label={order.customer_phone}
                                                    color="primary"
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                                                {formatCurrency(order.total_amount)}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'flex-end',
                                                    gap: 0.5
                                                }}>
                                                    {getBalanceIcon(remainingBalance)}
                                                    <Typography sx={{ 
                                                        fontWeight: 'bold',
                                                        color: remainingBalance <= 0 ? 'success.main' : 'warning.main'
                                                    }}>
                                                        {formatCurrency(remainingBalance)}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={order.payment_method?.toUpperCase()}
                                                    color={order.payment_method === 'cash' ? 'success' : 
                                                        order.payment_method === 'mobile_money' ? 'primary' : 'secondary'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={isFullyPaid ? 'PAID' : order.order_status?.toUpperCase()}
                                                    color={isFullyPaid ? 'success' : getStatusColor(order.order_status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Actions">
                                                    <IconButton 
                                                        sx={{ color: theme.palette.action.main }}
                                                        onClick={(e) => handleActionMenuOpen(e, order)}
                                                        size="small"
                                                    >
                                                        <MoreVert />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                            
                            {/* Footer with Totals */}
                            {filteredData.length > 0 && (
                                <TableBody>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.light + '20',
                                        borderTop: '3px solid',
                                        borderTopColor: theme.palette.primary.main,
                                        '& td': {
                                            fontWeight: 'bold',
                                            py: 2.5
                                        }
                                    }}>
                                        <TableCell colSpan={4} align="right">
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                                Totals ({filteredTotals.totalCount} loans):
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                                                        {formatCurrency(filteredTotals.totalLoanAmount)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Total Loan Amount
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                                        {formatCurrency(filteredTotals.totalOutstanding)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Total Outstanding
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell colSpan={1}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Paid:
                                                    </Typography>
                                                    <Chip
                                                        label={formatCurrency(filteredTotals.totalPaid)}
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                    />
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell colSpan={2}>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <Chip
                                                    icon={<TrendingUp />}
                                                    label={`${filteredTotals.paidLoansCount} Paid`}
                                                    size="small"
                                                    color="success"
                                                />
                                                <Chip
                                                    icon={<TrendingDown />}
                                                    label={`${filteredTotals.pendingLoansCount} Pending`}
                                                    size="small"
                                                    color="warning"
                                                />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ mt: 2 }}
                    />

                    {filteredData.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 8,
                            color: 'text.secondary'
                        }}>
                            <AccountBalance sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No loan records found</Typography>
                            <Typography variant="body2">Try adjusting your search or filter criteria</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Add Payment Dialog with Searchable Loan Selection */}
            <Dialog
                open={openAddPayment}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseAddPayment();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                scroll="paper"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>{selectedRecord && !isGenericPayment ? `Add Payment for ${selectedRecord.customer_name}` : 'Add Payment'}</b>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmitPayment} sx={{ mt: 2 }}>
                        {isGenericPayment && (
                            <>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>
                                    Select Loan
                                </Typography>
                                
                                <Autocomplete
                                    id="loan-select"
                                    options={pageState.ordersData.filter(order => 
                                        !isLoanFullyPaid(order) && order.order_status !== 'cancelled'
                                    )}
                                    loading={loading}
                                    value={pageState.ordersData.find(o => o.id === paymentForm.order_id) || null}
                                    onChange={handleLoanSelect}
                                    inputValue={loanSearchInput}
                                    onInputChange={(event, newInputValue) => {
                                        setLoanSearchInput(newInputValue);
                                    }}
                                    getOptionLabel={(option) => `${option.customer_name} - Loan #${option.id} - Balance: ${formatCurrency(getRemainingBalance(option))}`}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderOption={(props, option) => (
                                        <li {...props}>
                                            <Box>
                                                <Typography variant="body1">
                                                    {option.customer_name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Loan #: {option.id} | Balance: {formatCurrency(getRemainingBalance(option))} | 
                                                    Phone: {option.customer_phone}
                                                </Typography>
                                            </Box>
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Search Loan *"
                                            placeholder="Type customer name or loan ID..."
                                            required
                                            error={!!paymentErrors.order_id}
                                            helperText={paymentErrors.order_id ? "Please select a loan" : ""}
                                            sx={{ mb: 2 }}
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        <InputAdornment position="start">
                                                            <Search />
                                                        </InputAdornment>
                                                        {params.InputProps.startAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </>
                        )}
                        
                        {selectedRecord && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    Customer: <strong>{selectedRecord.customer_name}</strong>
                                </Typography>
                                <Typography variant="body2">
                                    Phone: <strong>{selectedRecord.customer_phone}</strong>
                                </Typography>
                                <Typography variant="body2">
                                    Loan Amount: <strong>{formatCurrency(selectedRecord.total_amount)}</strong>
                                </Typography>
                                <Typography variant="body2">
                                    Remaining Balance: <strong>{formatCurrency(getRemainingBalance(selectedRecord))}</strong>
                                </Typography>
                            </Alert>
                        )}
                        
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>
                            Payment Details
                        </Typography>
                        
                        <TextField
                            fullWidth
                            label="Payment Amount *"
                            name="amount"
                            type="number"
                            value={paymentForm.amount}
                            onChange={handlePaymentFormChange}
                            required
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AttachMoney />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Payment Method *</InputLabel>
                            <Select
                                name="payment_method"
                                value={paymentForm.payment_method}
                                onChange={handlePaymentFormChange}
                                label="Payment Method *"
                            >
                                <MenuItem value="cash">Cash</MenuItem>
                                <MenuItem value="mobile_money">Mobile Money</MenuItem>
                                <MenuItem value="bank">Bank</MenuItem>
                            </Select>
                        </FormControl>
                        
                        <TextField
                            fullWidth
                            label="Receipt Number (Optional)"
                            name="receipt_number"
                            value={paymentForm.receipt_number}
                            onChange={handlePaymentFormChange}
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Receipt />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        
                        <TextField
                            fullWidth
                            label="Payment Date"
                            name="payment_date"
                            type="date"
                            value={paymentForm.payment_date}
                            onChange={handlePaymentFormChange}
                            InputLabelProps={{ shrink: true }}
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CalendarMonth />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        
                        <TextField
                            fullWidth
                            label="Notes (Optional)"
                            name="notes"
                            value={paymentForm.notes}
                            onChange={handlePaymentFormChange}
                            multiline
                            rows={3}
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Note />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseAddPayment}
                        variant="contained"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmitPayment}
                        color="primary"
                        variant="contained"
                        disabled={!paymentForm.order_id && !selectedRecord}
                    >
                        Add Payment
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Payments Dialog */}
            <Dialog
                open={openViewPayments}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseViewPayments();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Payment History - {selectedRecord?.customer_name}</b>
                </DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <>
                            <Box sx={{ mb: 3, p: 2, backgroundColor: theme.palette.grey[50], borderRadius: 2 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            Loan Amount
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            {formatCurrency(selectedRecord.total_amount)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Paid
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                            {formatCurrency(getTotalPayments(selectedRecord.id) + parseFloat(selectedRecord.customer_given_cash || 0))}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            Remaining Balance
                                        </Typography>
                                        <Typography variant="h6" sx={{ 
                                            fontWeight: 'bold',
                                            color: getBalanceColor(getRemainingBalance(selectedRecord))
                                        }}>
                                            {formatCurrency(getRemainingBalance(selectedRecord))}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Payment History
                            </Typography>
                            
                            {pageState.selectedOrderPayments.length > 0 ? (
                                <TableContainer component={Paper} elevation={0}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ 
                                                backgroundColor: theme.palette.primary.main,
                                                '& th': {
                                                    color: 'white',
                                                    fontWeight: 'bold',
                                                    fontSize: '0.95rem'
                                                }
                                            }}>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Amount</TableCell>
                                                <TableCell>Method</TableCell>
                                                <TableCell>Receipt #</TableCell>
                                                <TableCell>Notes</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {pageState.selectedOrderPayments.map((payment, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        {formatCreatedAtDate(payment.created_at)}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                                        {formatCurrency(payment.amount)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={payment.payment_method?.toUpperCase()}
                                                            color={payment.payment_method === 'cash' ? 'success' : 
                                                                   payment.payment_method === 'mobile_money' ? 'primary' : 'secondary'}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {payment.receipt_number || 'N/A'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {payment.notes || 'N/A'}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography color="text.secondary">
                                        No payment history found for this loan
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseViewPayments}
                        color="error"
                        variant="contained"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Filter Options Dialog */}
            <Dialog
                open={filterOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseOptions();
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Loans
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<TodayIcon />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show Today's Loans
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<CalendarMonth />}
                            onClick={applyThisMonthFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show This Month's Loans
                        </Button>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Fields
                        </Typography>

                        <TextField
                            fullWidth
                            label="Customer Name"
                            name="customerName"
                            value={filters.customerName}
                            onChange={handleFilterChange}
                        />

                        <TextField
                            fullWidth
                            label="Customer Phone"
                            name="customerPhone"
                            value={filters.customerPhone}
                            onChange={handleFilterChange}
                        />

                        <FormControl fullWidth>
                            <InputLabel>Loan Status</InputLabel>
                            <Select
                                name="orderStatus"
                                value={filters.orderStatus}
                                onChange={handleFilterChange}
                                label="Loan Status"
                            >
                                <MenuItem value="">All Status</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="completed">Completed</MenuItem>
                                <MenuItem value="quotation">Quotation</MenuItem>
                                <MenuItem value="cancelled">Cancelled</MenuItem>
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseOptions} variant="contained">
                        Close
                    </Button>
                    <Button onClick={applyFilters} color="primary" variant="contained">
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Loan Details Dialog */}
            <Dialog 
                open={openView} 
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseView();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
                scroll="paper"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Loan Details - #{selectedRecord?.id}</b>
                </DialogTitle>
                
                <DialogContent>
                    {selectedRecord && (
                        <>
                            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                                <Tab label="Details" icon={<Description />} iconPosition="start" />
                                <Tab label="Items" icon={<Receipt />} iconPosition="start" />
                                <Tab label="Payments" icon={<Payment />} iconPosition="start" />
                            </Tabs>

                            {activeTab === 0 && (
                                <Box sx={{ p: 2 }}>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        mb: 3,
                                        p: 2,
                                        backgroundColor: theme.palette.grey[100],
                                        borderRadius: 2
                                    }}>
                                        <Avatar sx={{ 
                                            backgroundColor: theme.palette.primary.light,
                                            color: theme.palette.primary.main,
                                            mr: 2
                                        }}>
                                            <Person />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {selectedRecord.customer_name}
                                            </Typography>
                                            <Typography variant="body2">
                                                <Phone sx={{ verticalAlign: 'middle', fontSize: 16, mr: 0.5 }} />
                                                {selectedRecord.customer_phone}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    
                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                        <Grid item xs={12} sm={6}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Loan Status</Typography>
                                                <Chip
                                                    label={selectedRecord.order_status?.toUpperCase()}
                                                    color={getStatusColor(selectedRecord.order_status)}
                                                    size="small"
                                                    sx={{ mt: 1 }}
                                                />
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                                                <Chip
                                                    label={selectedRecord.payment_method?.toUpperCase()}
                                                    color={selectedRecord.payment_method === 'cash' ? 'success' : 
                                                           selectedRecord.payment_method === 'mobile_money' ? 'primary' : 'secondary'}
                                                    size="small"
                                                    sx={{ mt: 1 }}
                                                />
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Loan Amount</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                                                    {formatCurrency(selectedRecord.total_amount)}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Remaining Balance</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {getBalanceIcon(getRemainingBalance(selectedRecord))}
                                                    <Typography variant="h6" sx={{ 
                                                        fontWeight: 'bold',
                                                        color: getBalanceColor(getRemainingBalance(selectedRecord))
                                                    }}>
                                                        {formatCurrency(getRemainingBalance(selectedRecord))}
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    </Grid>

                                    {/* System Information - Card Style */}
                                    <SystemInformation
                                        createdBy={selectedRecord.created_by}
                                        updatedBy={selectedRecord.updated_by}
                                        createdAt={selectedRecord.created_at}
                                        updatedAt={selectedRecord.updated_at}
                                        title="Customer Loan System Information"
                                    />
                                </Box>
                            )}

                            {activeTab === 1 && (
                                <Box sx={{ p: 2 }}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                        Loan Items
                                    </Typography>
                                    
                                    <TableContainer component={Paper} elevation={0}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ 
                                                    backgroundColor: theme.palette.primary.main,
                                                    '& th': {
                                                        color: 'white',
                                                        fontWeight: 'bold',
                                                        fontSize: '0.95rem'
                                                    }
                                                }}>
                                                    <TableCell>Product</TableCell>
                                                    <TableCell align="center">Unit</TableCell>
                                                    <TableCell align="right">Price</TableCell>
                                                    <TableCell align="right">Quantity</TableCell>
                                                    <TableCell align="right">Total</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {selectedRecord.items && (() => {
                                                    try {
                                                        const items = typeof selectedRecord.items === 'string' 
                                                            ? JSON.parse(selectedRecord.items) 
                                                            : selectedRecord.items;
                                                        
                                                        return items.map((item, index) => {
                                                            // Get unit name
                                                            let unitDisplay = 'N/A';
                                                            if (item.unit_name && item.unit_name !== 'N/A') {
                                                                unitDisplay = item.unit_name;
                                                            } else if (item.unit_id) {
                                                                unitDisplay = getUnitName(item.unit_id);
                                                            } else if (item.unit) {
                                                                unitDisplay = item.unit;
                                                            }
                                                            
                                                            return (
                                                                <TableRow key={index}>
                                                                    <TableCell>{item.name}</TableCell>
                                                                    <TableCell align="center">
                                                                        <Chip
                                                                            icon={<Scale fontSize="small" />}
                                                                            label={unitDisplay}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            color="info"
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                                                                    <TableCell align="right">{item.quantity}</TableCell>
                                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                                        {formatCurrency(item.price * item.quantity)}
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        });
                                                    } catch (e) {
                                                        return (
                                                            <TableRow>
                                                                <TableCell colSpan={5} align="center">
                                                                    Error loading items
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    }
                                                })()}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}

                            {activeTab === 2 && (
                                <Box sx={{ p: 2 }}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                        Payment History
                                    </Typography>

                                    {pageState.selectedOrderPayments.length > 0 ? (
                                        <>
                                            <TableContainer component={Paper} elevation={0}>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow sx={{ 
                                                            backgroundColor: theme.palette.primary.main,
                                                            '& th': {
                                                                color: 'white',
                                                                fontWeight: 'bold',
                                                                fontSize: '0.95rem'
                                                            }
                                                        }}>
                                                            <TableCell>Date</TableCell>
                                                            <TableCell>Amount</TableCell>
                                                            <TableCell>Method</TableCell>
                                                            <TableCell>Receipt #</TableCell>
                                                            <TableCell>Notes</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {pageState.selectedOrderPayments.map((payment, index) => (
                                                            <TableRow key={index}>
                                                                <TableCell>{formatCreatedAtDate(payment.created_at)}</TableCell>
                                                                <TableCell sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                                                    {formatCurrency(payment.amount)}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={payment.payment_method?.toUpperCase()}
                                                                        color={payment.payment_method === 'cash' ? 'success' : 
                                                                               payment.payment_method === 'mobile_money' ? 'primary' : 'secondary'}
                                                                        size="small"
                                                                    />
                                                                </TableCell>
                                                                <TableCell>{payment.receipt_number || 'N/A'}</TableCell>
                                                                <TableCell>{payment.notes || 'N/A'}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>

                                            <Box sx={{ 
                                                p: 2, 
                                                backgroundColor: theme.palette.info.light + '20',
                                                borderRadius: 2,
                                                mt: 2
                                            }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                        Total Paid:
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                                        {formatCurrency(getTotalPayments(selectedRecord.id) + parseFloat(selectedRecord.customer_given_cash || 0))}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </>
                                    ) : (
                                        <Box sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography color="text.secondary">
                                                No payment history found for this loan
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>

                <DialogActions>
                    {hasPermission('ADD') && selectedRecord && !isLoanFullyPaid(selectedRecord) && (
                        <Button
                            onClick={() => handleOpenAddPayment(selectedRecord)}
                            color="success"
                            variant="contained"
                            startIcon={<AttachMoney />}
                        >
                            Add Payment
                        </Button>
                    )}
                    {hasPermission('PRINT') && selectedRecord && (
                        <Button onClick={() => handlePrint(selectedRecord)} color="info" variant="contained">
                            <Print sx={{ mr: 1 }} /> Print
                        </Button>
                    )}
                    <Button onClick={handleCloseView} color="error" variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default CustomerLoans;